<template>
    <div ref="popperRef" class="position-relative" @mouseleave.stop="mouseLeave" :class="{ 'd-inline-flex': type === 'sort' }">
        <div @click="toggle" ref="activator" @mouseover.stop="(e) => mouseEnter(e)" class="activator" :class="{'pointer': !automaticOpen && !disabled }">
            <slot></slot>
        </div>
        <div v-if="isOpen" ref="popperContent" class="custom-popper" :class="{ 'sort': type === 'sort', 'info': isBtnOrIcon, 'min-width-auto': minWidthAuto, 'invalid-popper': invalid }" :style="{ maxWidth: maxWidth && `${maxWidth}px` }">
            <div class="element"></div>
            <div @click="toggle" class="text-content neue-roman">
                <div v-if="contentHasHtml" v-html="contentText" />
                <slot v-else name="content" />
            </div>
        </div>
    </div>
</template>

<script>
import { nextTick } from 'vue'

export default {
    props: {
        hover: {
            type: Boolean,
            default: false
        },
        type: {
            type: String,
            default: 'info'
        },
        disabled: {
            type: Boolean,
            default: false
        },
        offset: {
            type: [Number, String],
            default: 0
        },
        offsetY: {
            type: Number,
            default: 0
        },
        startleft: {
            type: Boolean,
            default: false
        },
        top: {
            type: Boolean,
            default: false
        },
        maxWidth: {
            type: [Number, Boolean],
            default: false
        },
        minWidthAuto: {
            type: Boolean,
            default: false
        },
        // Размещает блок с контентом по центру активатора и стрелочку по центру блока с контентом
        // startleft должен быть false
        centralize: {
            type: Boolean,
            default: false
        },
        // Для показа ошибок валидации в элементах формы
        automaticOpen: {
            type: Boolean,
            default: false
        },
        show: {
            type: [Boolean, String],
        },
        // Стиль для сообщений об ошибках (красный фон)
        invalid: {
            type: Boolean,
            default: false
        },
        // Позиционирование сбоку от модального окна (слева)
        modalSide: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            isOpen: false,  
            fromLeft: this.startleft,
            offsetXContent: this.offset,
            offsetYContent: this.offsetY,
            isBtnOrIcon: false
        }
    },
    computed: {
        contentText() {
            const slot = this.$slots.content?.();
            if (!slot || slot.length === 0) return '';
            
            // Извлекаем текст из VNode
            const firstNode = slot[0];
            if (firstNode.children && typeof firstNode.children === 'string') {
                return firstNode.children;
            }
            return '';
        },
        contentHasHtml() {
            const text = this.contentText;
            if (!text) return false;
            
            // Проверяем наличие HTML тегов
            const htmlRegex = /<\/?[a-z][\s\S]*>/i;
            return htmlRegex.test(text);
        }
    },
    watch: {
        show: {
            handler(newVal) {
                if(this.automaticOpen) {
                    this.isOpen = newVal;
                }
            },
            immediate: true
        },
        isOpen: {
            handler() {
                if(this.isOpen) {
                    nextTick(() => {
                        const rect = this.$refs.popperRef.getBoundingClientRect();
                        const contentWidth = this.$refs.popperContent.offsetWidth + this.offsetXContent;
                        let contentHeight = this.$refs.popperContent.offsetHeight;
                        const windowHeight = window.innerHeight;
                        
                        const spaceBelow = windowHeight - rect.bottom;
                        const spaceAbove = rect.top;
                        
                        this.$refs.popperContent.classList.remove('left', 'top', 'break-word', 'modal-side');

                        if (this.modalSide) {
                            this.$refs.popperContent.classList.add('modal-side');
                            const modalWindow = this.$refs.popperRef.closest('.vue-modal-window');
                            if (modalWindow) {
                                const modalRect = modalWindow.getBoundingClientRect();
                                const activatorRect = this.$refs.popperRef.getBoundingClientRect();
                                const contentEl = this.$refs.popperContent;
                                const contentW = contentEl.offsetWidth;
                                // Position to the left of the modal window
                                const leftOffset = activatorRect.left - modalRect.left + contentW + 16;
                                contentEl.style.left = `-${leftOffset}px`;
                                contentEl.style.right = 'auto';
                                // Vertically align with activator
                                contentEl.style.top = '0px';
                            }
                            return;
                        }

                        if(this.centralize && rect.right >= contentWidth && !this.fromLeft) {
                            const content = this.$refs.popperContent;
                            content.style.left = '50%';
                            content.style.transform = 'translateX(-50%)';
                            // 12.5px - половина ширины .element
                            const element = this.$refs.popperContent.querySelector('.element');
                            element.style.right = `calc(50% - ${12.5}px)`;
                        }
                        
                        if(rect.right <= contentWidth || this.fromLeft) {
                            this.fromLeft = true;
                            this.$refs.popperContent.classList.add('left');
                        } else {
                            this.fromLeft = false;
                        }
                        
                        if(this.fromLeft && (contentWidth > window.innerWidth - this.$refs.popperRef.getBoundingClientRect().left)) {
                            this.$refs.popperContent.classList.add('break-word');
                            contentHeight = this.$refs.popperContent.offsetHeight;
                        }

                        if(this.fromLeft) {
                            const popperRect = this.$refs.popperContent.getBoundingClientRect();
                            if(popperRect.left < 0) {
                                this.$refs.popperContent.style.left = `${-rect.left + 8}px`;
                            }
                        }
                        
                        if(spaceBelow < contentHeight + 10 && spaceAbove > contentHeight + 10 || this.top) {
                            this.$refs.popperContent.classList.add('top');
                        }
                        
                        if((this.offset || this.offsetY) && !this.fromLeft) {
                            if(String(this.offsetXContent).includes('%')) {
                                this.$refs.popperContent.style.right = this.offsetXContent;
                            } else {
                                this.$refs.popperContent.style.right = `${this.offsetXContent}px`;
                            }

                            this.$refs.popperContent.style.top = this.$refs.activator.getBoundingClientRect().height + this.offsetYContent + 'px';
                        } else if((this.offset || this.offsetY) && this.fromLeft) {
                            if(String(this.offsetXContent).includes('%')) {
                                this.$refs.popperContent.style.left = this.offsetXContent;
                            } else {
                                this.$refs.popperContent.style.left = `${this.offsetXContent}px`;
                            }
                            
                            this.$refs.popperContent.style.top = this.$refs.activator.getBoundingClientRect().height + this.offsetYContent + 'px';
                        }
                        

                        
                        const activatorBtnIcon = this.$refs.popperRef.querySelector('.activator > *').classList.contains('icon') || 
                        this.$refs.popperRef.querySelector('.activator > *').classList.contains('btn');
                        this.isBtnOrIcon = activatorBtnIcon;
                        
                        if ((activatorBtnIcon || this.type === 'info-table') && !this.fromLeft && !this.offset) {
                            const activator = this.$refs.popperRef.querySelector('.activator > *');
                            const activatorRect = activator.getBoundingClientRect();
                            const content = this.$refs.popperContent;

                            const iconCenter = (activatorRect.width) / 2 + 1;
                            // Первое число - left/right у .custom-popper, второе - left/right у .element
                            if(this.fromLeft) {
                                content.style.right = `${iconCenter - 10 - 14}px`;
                            } else {
                                content.style.right = `${iconCenter - 14 - 10}px`;
                            }       
                        }
                    })
                } else {
                    if(!this.startleft) {
                        this.fromLeft = false;
                    }
                }
            },
            immediate: true
        }
    },
    methods: {
        toggle() {
            if(!this.disabled && !this.hover && !this.automaticOpen) {
                this.isOpen = !this.isOpen
            }
        },
        handleClickOutside(event)  {
            if(this.automaticOpen) {
                return;
            }
            const el = this.$refs.popperRef
            if (el && !el.contains(event.target)) {
                this.isOpen = false
            }
        },
        mouseEnter(e) {
            if(this.disabled) {
                return
            }
            const el = this.$refs.popperRef
            if (el && el.contains(e.target) && this.hover) {
                if(this.isOpen) {
                    return
                }
                this.isOpen = !this.isOpen
            }
        },
        mouseLeave() {
            if(this.hover && !this.disabled) {
                this.isOpen = false
            }
        },
    },
    mounted() {

        document.addEventListener('click', this.handleClickOutside)
    },
    beforeUnmount() {
        document.removeEventListener('click', this.handleClickOutside)
    }
}
</script>

<style lang="scss" scoped>
.custom-popper {
    position: absolute;
    background-color: $menu-bg;
    border-radius: 8px;
    padding: 12px;
    top: calc(100% + 5px);
    right: -14px;
    z-index: 8000;
    min-width: 200px;

    .element {
        width: 25px;
        height: 25px;
        background-color: $menu-bg;
        transform: rotate(45deg);
        position: absolute;
        top: 0;
        z-index: -1;
        right: 10px;
    }

    &.left {
        left: -10px;
        right: auto;

        .element {
            right: auto;
            left: 14px;
        }
    }

    &.top {
        top: auto;
        bottom: calc(100% + 5px);

        .element {
            top: auto;
            bottom: 0;
        }
    }

    &.top.left {
        .element {
            right: auto;
            left: 10px;
        }
    }

    &.sort {
        max-width: none;
        padding: 4px;
    }
    &.info {
        max-width: 200px;
        width: max-content;
        min-width: none;
    }
    &.min-width-auto {
        min-width: auto;
    }
    
    &.break-word {
        max-width: 90vw;
        word-break: break-all;
        overflow-wrap: break-word;
    }

    &.invalid-popper {
        background-color: $color-invalid;

        .element {
            background-color: $color-invalid;
        }
    }

    &.modal-side {
        max-width: 280px;
        width: max-content;

        .element {
            left: auto;
            right: -5px;
            top: 16px;
        }
    }
}
.text-content {
    font-size: 14px;
    color: #ffffff !important;
    
    :deep(a) {
        color: white !important;
        text-decoration: underline !important;
        cursor: pointer;
    }
}

</style>